import {createContext, use, useMemo, useState, type Dispatch, type SetStateAction} from 'react'
import type {LogEntry, Session} from '../types/session'

interface SessionContextType {
  session: Session
  copilotApiUrl?: string
  logs?: LogEntry[]
  logsAreLoading?: boolean
  mcpToolNameMappings: Record<string, string>
  setMcpToolNameMappings: Dispatch<SetStateAction<Record<string, string>>>
  taskSharingStatus?: 'unshared' | 'shared'
  reconciledAiCredits?: number
}

export const SessionContext = createContext<SessionContextType | undefined>(undefined)

export function useSessionContext() {
  const context = use(SessionContext)
  if (!context) {
    throw new Error('useSessionContext must be used within a SessionContextProvider')
  }
  return context
}

interface SessionContextProviderProps {
  session: Session
  copilotApiUrl?: string
  logs?: LogEntry[]
  logsAreLoading?: boolean
  taskSharingStatus?: 'unshared' | 'shared'
  reconciledAiCredits?: number
  children: React.ReactNode
}

export function SessionContextProvider({
  session,
  copilotApiUrl,
  logs,
  logsAreLoading,
  taskSharingStatus,
  reconciledAiCredits,
  children,
}: SessionContextProviderProps) {
  const [mcpToolNameMappings, setMcpToolNameMappings] = useState<Record<string, string>>({})
  const value = useMemo(
    () => ({
      session,
      copilotApiUrl,
      logs,
      logsAreLoading,
      mcpToolNameMappings,
      setMcpToolNameMappings,
      taskSharingStatus,
      reconciledAiCredits,
    }),
    [session, copilotApiUrl, logs, logsAreLoading, mcpToolNameMappings, taskSharingStatus, reconciledAiCredits],
  )
  return <SessionContext value={value}>{children}</SessionContext>
}
